import { create } from "zustand";

export interface ReindexProgressEvent {
  runId: string;
  indexName: string;
  processed: number;
  total: number;
  message: string;
  status: "Started" | "InProgress" | "Completed" | "Failed" | "TimedOut";
}

/** How long to keep believing a just-triggered run is starting, before
 *  accepting whatever the server reports. Covers the gap between the trigger
 *  returning 202 and the background runner registering the run — without it a
 *  status poll landing in that gap would report "nothing running" and the
 *  progress modal would go dead for the whole run. */
const STARTING_GRACE_MS = 30_000;

interface ReindexState {
  inProgress: boolean;
  indexName: string | null;
  processed: number;
  total: number;
  messages: string[];
  failed: boolean;
  /** Triggered, but the server hasn't reported the run yet. */
  starting: boolean;
  startedAt: number | null;

  /** Called the moment a reindex is triggered: clears the previous run so its
   *  index name, log and progress can't be mistaken for the new one. */
  begin: (indexName: string) => void;
  hydrate: (status: {
    inProgress: boolean;
    indexName: string | null;
    processed: number;
    total: number;
    status?: string | null;
    lastMessage?: string | null;
  }) => void;
  applyEvent: (event: ReindexProgressEvent) => void;
  reset: () => void;
}

const EMPTY = {
  inProgress: false,
  indexName: null,
  processed: 0,
  total: 0,
  messages: [] as string[],
  failed: false,
  starting: false,
  startedAt: null,
};

/** Appends a server message to the log without repeating it.
 *
 * The status endpoint only ever returns the *latest* message, and it is polled
 * every few seconds, so the same line comes back many times over. Replacing
 * the log with it (which is what used to happen) meant the modal only ever
 * showed one line no matter how long the run took. */
function appendMessage(existing: string[], message?: string | null): string[] {
  if (!message) return existing;
  if (existing[existing.length - 1] === message) return existing;
  return [...existing, message];
}

export const useReindexStore = create<ReindexState>((set) => ({
  ...EMPTY,

  begin: (indexName) =>
    set({
      ...EMPTY,
      indexName,
      inProgress: true,
      starting: true,
      startedAt: Date.now(),
    }),

  // The live SignalR feed may be down or may have missed earlier events
  // (e.g. this page just loaded) — seed the log from the last known
  // server-side message/status so the modal isn't just a blank "waiting"
  // state when a run is genuinely stuck or already finished.
  hydrate: (status) =>
    set((s) => {
      // A run was just triggered and the server is still reporting the
      // previous (or no) run — ignore it rather than let it declare the new
      // run finished before it has started. Bounded, so a trigger the server
      // silently dropped can't leave the modal spinning forever.
      const withinGrace =
        s.starting &&
        s.startedAt != null &&
        Date.now() - s.startedAt < STARTING_GRACE_MS;

      if (withinGrace && status.indexName !== s.indexName) return s;

      // A different index than the one being shown means a different run —
      // its log doesn't belong under this one.
      const sameRun = status.indexName === s.indexName;

      return {
        inProgress: status.inProgress,
        indexName: status.indexName,
        processed: status.processed,
        total: status.total,
        messages: sameRun
          ? appendMessage(s.messages, status.lastMessage)
          : appendMessage([], status.lastMessage),
        failed: status.status === "Failed" || status.status === "TimedOut",
        starting: false,
        startedAt: sameRun ? s.startedAt : null,
      };
    }),

  applyEvent: (event) =>
    set((s) => {
      const sameRun = event.indexName === s.indexName;

      return {
        inProgress:
          event.status === "Started" || event.status === "InProgress",
        indexName: event.indexName,
        processed: event.processed,
        total: event.total,
        failed: event.status === "Failed" || event.status === "TimedOut",
        messages: sameRun
          ? appendMessage(s.messages, event.message)
          : appendMessage([], event.message),
        starting: false,
        startedAt: sameRun ? s.startedAt : null,
      };
    }),

  reset: () => set({ ...EMPTY }),
}));
